import React, { useEffect, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import { Layout } from "src/components/layout"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import { useAuth } from "src/providers/LoginProvider"
import { useRecoilState } from "recoil"
import { Tag, TagService, User, UserService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import BreadCrumb from "src/components/atom/BreadCrumb"
import EmployeeDetailItem from "./EmployeeDetailItem"
import {
  EMPLOYMENT_TYPE,
  Genders,
  SIDE_MENU,
  SNACKBAR_TYPES,
  STATUS,
  USER_TYPES,
} from "src/modules/const"
import "./index.scss"
import { EmployeeDeleteModal } from "./EmployeeDeleteModal"
import { Top } from "../top"

export const EmployeeDetail = () => {
  const { t } = useTranslation("translation", { keyPrefix: "employee" })

  const [userInfo, setUserInfo] = useState<User>()

  const [, setShowProgress] = useRecoilState(ShowProgress)
  const [, setShowToast] = useRecoilState(ShowToast)

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)

  const { companyId, id } = useParams()

  const navigate = useNavigate()

  const { user } = useAuth()

  const [tags, setTags] = useState<Tag[]>([])
  useEffect(() => {
    if (user == null) {
      return
    }
    TagService.listTags(user.token, Number(companyId))
      .then((res) => {
        setTags(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
  }, [user, companyId])

  const idToTag = new Map(tags.map((tag) => [tag.id, tag.tag]))

  const handleGetEmployee = async () => {
    if (!user) {
      return
    }
    setShowProgress(true)
    UserService.getUser(user.token, Number(companyId), Number(id!))
      .then((res) => {
        setShowProgress(false)
        setUserInfo(res)
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_detail"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const calcAge = (date: string) => {
    let age = new Date().getFullYear() - new Date(date as string).getFullYear()
    return age.toString()
  }

  const getType = (type: string) => {
    let v = ""
    EMPLOYMENT_TYPE.forEach((t) => {
      if (type == t.value) {
        v = t.name
      }
    })
    return v
  }

  const getUserType = (userType: string) => {
    let v = ""
    USER_TYPES.forEach((t) => {
      if (userType === t.value) {
        v = t.name
      }
    })
    return v
  }

  const handleDelete = () => {
    if (!user) {
      return
    }
    setShowProgress(true)

    UserService.deleteUser(user.token, Number(companyId), Number(id!))
      .then(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("success_delete"), type: SNACKBAR_TYPES.SUCCESS })
        navigate(`/company/${companyId}/employee`)
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_delete"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  useEffect(() => {
    if (user == null) {
      return
    }
    handleGetEmployee()
  }, [companyId, id, user])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.EMPLOYEE_LIST}>
      <BreadCrumb
        paths={[
          {
            name: t("employee_list"),
            link: `/company/${companyId}/employee`,
          },
          {
            name:
              userInfo?.lastName == undefined || userInfo?.firstName == undefined
                ? ""
                : `${userInfo?.lastName} ${userInfo?.firstName} （${userInfo?.lastNameKana} ${userInfo?.firstNameKana}）`,
            link: "",
          },
        ]}
      />
      <Box className="employee-content-container">
        <Box className="employee-subtitle">
          <Typography className="employee-info">{t("employee_info")}</Typography>
          <Box className="employee-info-buttons">
            <Button
              className="employee-edit-button"
              onClick={() => {
                navigate(`/company/${companyId}/employee/edit/${id}`)
              }}
            >
              {t("edit")}
            </Button>
            <Button
              variant="outlined"
              className="employee-delete-button"
              onClick={() => setDeleteModalOpen(true)}
            >
              {t("delete")}
            </Button>
          </Box>
        </Box>
        <EmployeeDetailItem label={t("ID")} value={userInfo?.employeeId!.toString()} />
        <EmployeeDetailItem
          label={t("name_abbreviation")}
          value={userInfo ? `${userInfo?.lastName} ${userInfo?.firstName}` : ""}
        />
        <EmployeeDetailItem
          label={t("name_kana")}
          value={userInfo ? `${userInfo?.lastNameKana} ${userInfo?.firstNameKana}` : ""}
        />
        <EmployeeDetailItem
          label={t("gender")}
          value={
            userInfo
              ? userInfo?.gender == Genders.OTHER
                ? userInfo.genderComment
                : userInfo?.gender == Genders.MALE
                  ? t("male")
                  : t("female")
              : ""
          }
        />
        <EmployeeDetailItem label={t("birthday")} value={userInfo?.birthday} />
        <EmployeeDetailItem
          label={t("age")}
          value={userInfo?.birthday ? calcAge(userInfo?.birthday!) : ""}
        />
        <EmployeeDetailItem label={t("email_address")} value={userInfo?.email} />
        <EmployeeDetailItem label={t("post_code")} value={userInfo?.postCode} />
        <EmployeeDetailItem label={t("province")} value={userInfo?.province} />
        <EmployeeDetailItem label={t("city")} value={userInfo?.city} />
        <EmployeeDetailItem label={t("street")} value={userInfo?.address} />
        <EmployeeDetailItem label={t("building")} value={userInfo?.buildingNo} />
        <EmployeeDetailItem label={t("phone_number")} value={userInfo?.phoneNumber} />
        <EmployeeDetailItem label={t("join_company_date")} value={userInfo?.joiningDate} />
        <EmployeeDetailItem
          label={t("leave_company_date")}
          value={userInfo?.status == STATUS.RESIGNED ? userInfo?.resignDate : ""}
        />
        <EmployeeDetailItem
          label={t("employment_type")}
          value={getType(userInfo?.employmentType!)}
        />
        <EmployeeDetailItem
          label={t("tag")}
          tags={userInfo?.tagIds ? userInfo?.tagIds.map((id) => idToTag.get(id)) : undefined}
        />
        <EmployeeDetailItem label={t("user_type")} value={getUserType(userInfo?.userType!)} />
        <EmployeeDetailItem label={t("comment")} value={userInfo?.comment} />

        <EmployeeDeleteModal
          open={deleteModalOpen}
          handleOpen={setDeleteModalOpen}
          handleDelete={handleDelete}
        />
      </Box>
    </Layout>
  )
}
